<script>
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import { vec2, mat3 } from "gl-matrix";

  import {
    mode,
    seed,
    noiseFactor,
    reliefFactor,
    borderFactor,
    fakeHeightFactor,
    selectedOverlay,
    palette,
    overlays,
    canvasWidth,
    canvasHeight,
    waveStyle,
    waveOpacity,
    waveSpeed,
    waveScale,
    waveThickness,
    vignette,
    grain,
    resolutionScale,
    paletteName,
  } from "./stores/globalControls.js";

  import palettes from "./lib/palettes.js";
  import { gameIcons, loadGameIcons, ICON_VIEWBOX } from "./lib/stickers.js";
  import { flushUrl } from "./lib/share.js";
  import { view } from "./stores/view.js";

  import MenuButton from "./MenuButton.svelte";
  import PaletteInput from "./PaletteInput.svelte";
  import ControlPanel from "./ControlPanel.svelte";

  import ShuffleLine from "svelte-remixicon/lib/icons/ShuffleLine.svelte";
  import EarthLine from "svelte-remixicon/lib/icons/EarthLine.svelte";
  import BrushLine from "svelte-remixicon/lib/icons/BrushLine.svelte";
  import MapPin2Line from "svelte-remixicon/lib/icons/MapPin2Line.svelte";
  import QuestionLine from "svelte-remixicon/lib/icons/QuestionLine.svelte";
  import ArrowLeftLine from "svelte-remixicon/lib/icons/ArrowLeftLine.svelte";
  import MagicLine from "svelte-remixicon/lib/icons/MagicLine.svelte";
  import Compass3Line from "svelte-remixicon/lib/icons/Compass3Line.svelte";
  import MenuLine from "svelte-remixicon/lib/icons/MenuLine.svelte";
  import CloseLine from "svelte-remixicon/lib/icons/CloseLine.svelte";
  import FullscreenLine from "svelte-remixicon/lib/icons/FullscreenLine.svelte";
  import FullscreenExitLine from "svelte-remixicon/lib/icons/FullscreenExitLine.svelte";
  import ShareLine from "svelte-remixicon/lib/icons/ShareLine.svelte";

  let menuOpen = false;
  $: $paletteName !== "custom" && palette.set([...palettes[$paletteName]]);

  // Sticker picker: lazy-load the full game-icons set the first time the
  // Stickers panel is opened, then filter it by a search box.
  let stickerSearch = "";
  const STICKER_LIMIT = 240;
  $: if ($mode === "stickers") loadGameIcons();
  $: stickerQuery = stickerSearch.trim().toLowerCase();
  $: stickerMatches = $gameIcons
    ? stickerQuery
      ? $gameIcons.names.filter((n) => n.includes(stickerQuery))
      : $gameIcons.names
    : [];
  $: stickerResults = stickerMatches.slice(0, STICKER_LIMIT);

  // Map-space position of a screen-uv point under the current view. Sampled with
  // get() so it reads the live transform without subscribing Menu to it.
  function screenUvToMap(screenUv) {
    return vec2.transformMat3(vec2.create(), screenUv, get(view));
  }

  // A scale-`s` transform positioned at the current screen center, for placing
  // freshly added overlays where the user is looking rather than at a fixed point.
  function centeredTransform(s) {
    const c = screenUvToMap(vec2.fromValues(0.5, 0.5));
    return mat3.fromValues(s, 0, 0, 0, s, 0, c[0], c[1], s);
  }

  function addLabel(text, transform) {
    const overlay = {
      type: "label",
      name: "label" + $overlays.length,
      text,
      fontSizeRem: 2,
      letterSpacingRem: 0,
      zooming: 1.0,
      rotationDeg: 0,
      textAlign: "center",
      transform: transform || centeredTransform(1),
      anchor: "c",
      document: {
        position: vec2.create(),
        extent: vec2.create(),
        listeners: {},
      },
    };
    overlays.update((o) => [...o, overlay]);
    return overlay;
  }

  function addSticker(icon) {
    const overlay = {
      type: "sticker",
      name: "sticker" + $overlays.length,
      icon,
      color: "#ffffff",
      sizeRem: 4,
      zooming: 1.0,
      rotationDeg: 0,
      anchor: "c",
      transform: centeredTransform(1),
      document: {
        position: vec2.create(),
        extent: vec2.create(),
        listeners: {},
      },
    };
    overlays.update((o) => [...o, overlay]);
    return overlay;
  }

  function addTopoInfluence(factor, transform) {
    let overlay = {
      type: "topoInfluence",
      name: "topoInfluence" + $overlays.length,
      factor,
      color: "#ffffff",
      transform,
      document: {
        position: vec2.create(),
        extent: vec2.create(),
        listeners: {},
      },
    };
    overlays.update((o) => [...o, overlay]);
    return overlay;
  }

  addTopoInfluence(1.0, mat3.fromValues(0.6, 0, 0, 0, 0.6, 0, 0.4, 0.4, 0.6));
  addTopoInfluence(-1.0, mat3.fromValues(0.4, 0, 0, 0, 0.4, 0, 0.65, 0.65, 0.4));

  function randomize(e) {
    if (e.shiftKey) {
      fakeHeightFactor.set(0.0, { duration: 400 });
      setTimeout(() => {
        seed.set(Math.random());
        fakeHeightFactor.set(1.0, { duration: 1000 });
      }, 500);
    } else {
      seed.set(Math.random());
    }
  }

  function resetMode() {
    mode.set(null);
    selectedOverlay.set(null);
  }

  function deleteSelectedOverlay() {
    overlays.set($overlays.filter((o) => o !== $selectedOverlay));
    selectedOverlay.set(null);
  }

  // In-app clipboard for overlays. Works for any type since overlay data is
  // plain fields plus a transform; the live document and name are rebuilt fresh.
  const PASTE_OFFSET = 0.02; // map-space nudge so a copy doesn't cover the original
  let clipboard = null;
  let pasteCounter = 0;
  const pointer = { x: 0, y: 0 }; // latest cursor position, in client pixels

  function trackPointer(e) {
    pointer.x = e.clientX;
    pointer.y = e.clientY;
  }

  // The cursor in map space, or null if the canvas hasn't been sized yet
  // (screen-uv is cursor / canvas size).
  function pointerMapPosition() {
    const w = get(canvasWidth);
    const h = get(canvasHeight);
    if (!w || !h) return null;
    return screenUvToMap(vec2.fromValues(pointer.x / w, pointer.y / h));
  }

  // Detached, reusable copy of an overlay: drop the live document/name, deep-copy
  // the transform so edits to one don't alias the other.
  function detach(overlay) {
    const { document: _doc, name: _name, transform, ...rest } = overlay;
    return { ...rest, transform: mat3.clone(transform) };
  }

  // Place a detached overlay as a fresh, selected overlay. With a map position it
  // lands there (keyboard paste at the cursor); otherwise it's nudged off its source.
  function placeCopy(data, mapPos) {
    const transform = mat3.clone(data.transform);
    if (mapPos) {
      transform[6] = mapPos[0];
      transform[7] = mapPos[1];
    } else {
      transform[6] += PASTE_OFFSET;
      transform[7] += PASTE_OFFSET;
    }
    const overlay = {
      ...data,
      transform,
      name: `${data.type}-copy-${++pasteCounter}`,
      document: { position: vec2.create(), extent: vec2.create(), listeners: {} },
    };
    overlays.update((o) => [...o, overlay]);
    selectedOverlay.set(overlay);
    return overlay;
  }

  function copySelectedOverlay() {
    if ($selectedOverlay) clipboard = detach($selectedOverlay);
  }

  function pasteOverlay() {
    if (clipboard) placeCopy(clipboard, pointerMapPosition());
  }

  function duplicateSelectedOverlay() {
    if ($selectedOverlay) placeCopy(detach($selectedOverlay));
  }

  let isFullscreen = false;

  function syncFullscreen() {
    isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
  }

  function toggleFullscreen() {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    } else {
      const el = document.documentElement;
      const request = el.requestFullscreen || el.webkitRequestFullscreen;
      // Rejects if the browser blocks it (e.g. iOS Safari); ignore quietly.
      Promise.resolve(request.call(el)).catch(() => {});
    }
  }

  onMount(() => {
    syncFullscreen();
    // Stay in sync when the user toggles via F11 or Esc, not just our button.
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    };
  });

  let shareLabel = "Share";
  let shareLabelTimer = null;

  function flashShareLabel(text) {
    shareLabel = text;
    if (shareLabelTimer) {
      clearTimeout(shareLabelTimer);
    }
    shareLabelTimer = setTimeout(() => (shareLabel = "Share"), 1500);
  }

  async function shareUrl() {
    flushUrl(); // make sure the debounced URL reflects the latest map
    const url = location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Mythic Atlas", url });
      } catch (e) {
        // user dismissed the share sheet, or it failed; nothing to do
      }
      return;
    }
    // No Share API (e.g. desktop Firefox): fall back to copying the link.
    try {
      await navigator.clipboard.writeText(url);
      flashShareLabel("Link copied!");
    } catch (e) {
      flashShareLabel("Copy failed");
    }
  }

  const MODE_KEYS = {
    t: "topography",
    c: "colors",
    l: "labels",
    s: "stickers",
    e: "effects",
    h: "help",
  };

  const PAN_SPEED = 0.75; // viewport fractions/sec while an arrow key is held
  const SLOW_PAN_SCALE = 0.25; // Ctrl+arrow pans at a quarter speed for fine nudges
  const ZOOM_STEP = 0.2; // log-zoom per +/- press
  const ARROW_DIRS = {
    arrowleft: [-1, 0],
    arrowright: [1, 0],
    arrowup: [0, -1],
    arrowdown: [0, 1],
  };

  // Arrow keys pan continuously: track which are held and drive a per-frame
  // velocity in the view's animation loop, rather than nudging once per keypress.
  const heldArrows = new Set();
  let panSpeedScale = 1; // SLOW_PAN_SCALE while Ctrl is held, 1 otherwise
  function updatePan() {
    let vx = 0;
    let vy = 0;
    for (const k of heldArrows) {
      vx += ARROW_DIRS[k][0];
      vy += ARROW_DIRS[k][1];
    }
    const speed = PAN_SPEED * panSpeedScale;
    view.pan(vec2.fromValues(vx * speed, vy * speed));
  }

  function handleKeyUp(e) {
    if (heldArrows.delete(e.key.toLowerCase())) {
      updatePan();
    }
  }

  // Release keys if the window loses focus mid-press, so panning can't stick on.
  function clearPan() {
    if (heldArrows.size) {
      heldArrows.clear();
      updatePan();
    }
  }

  // Keyboard shortcuts. Typing in a field takes precedence; the clipboard chord
  // (Cmd/Ctrl+C/V) is handled before other modifier combos are left to the browser.
  function handleKey(e) {
    const el = e.target;
    if (
      el &&
      (el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.tagName === "SELECT" ||
        el.isContentEditable)
    ) {
      return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const chord = e.key.toLowerCase();
      if (chord === "c" && $selectedOverlay) {
        e.preventDefault();
        copySelectedOverlay();
        return;
      }
      if (chord === "v" && clipboard) {
        e.preventDefault();
        pasteOverlay();
        return;
      }
    }
    // Arrow keys pan; Ctrl pans slowly for fine nudges. Handled before the
    // modifier bail-out so Ctrl+arrow still works; Meta/Alt+arrow fall through to
    // the browser. Re-runs on key repeat so toggling Ctrl mid-pan takes effect.
    if (!e.metaKey && !e.altKey) {
      const arrow = e.key.toLowerCase();
      if (ARROW_DIRS[arrow]) {
        e.preventDefault();
        panSpeedScale = e.ctrlKey ? SLOW_PAN_SCALE : 1;
        heldArrows.add(arrow);
        updatePan();
        return;
      }
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === "Escape") {
      resetMode();
      return;
    }
    const key = e.key.toLowerCase();
    if (key === "delete" || key === "backspace") {
      if ($selectedOverlay) {
        e.preventDefault();
        deleteSelectedOverlay();
      }
      return;
    }
    if (key === "r") {
      randomize(e);
      return;
    }
    if (key === "f") {
      toggleFullscreen();
      return;
    }
    if (key === "o") {
      view.reset();
      return;
    }
    if (key === "+" || key === "=") {
      view.smoothZoom(Math.log(1 - ZOOM_STEP), vec2.fromValues(0.5, 0.5));
      return;
    }
    if (key === "-" || key === "_") {
      view.smoothZoom(Math.log(1 + ZOOM_STEP), vec2.fromValues(0.5, 0.5));
      return;
    }
    const target = MODE_KEYS[key];
    if (!target) return;
    // Pressing the open mode's key again toggles back to the main menu.
    if ($mode === target) {
      resetMode();
    } else {
      mode.set(target);
    }
  }
</script>

<svelte:window
  on:keydown={handleKey}
  on:keyup={handleKeyUp}
  on:blur={clearPan}
  on:pointermove={trackPointer}
/>

<div class="controls">
  {#if !$mode && !$selectedOverlay}
    <MenuButton
      icon={menuOpen ? CloseLine : MenuLine}
      label={menuOpen ? "Close menu" : "Menu"}
      on:click={() => (menuOpen = !menuOpen)}
    />
  {/if}
  {#if menuOpen || $mode || $selectedOverlay}
  {#if $mode && $mode !== "help"}
    <MenuButton icon={ArrowLeftLine} label="Back" on:click={resetMode} />
  {/if}
  {#if $mode === "topography"}
    <ControlPanel title="Topography" icon={EarthLine}>
      <label class="range">
        Warp
        <input type="range" min="0" max="1" step="any" bind:value={$noiseFactor} />
      </label>
      <label class="range">
        Relief
        <input type="range" min="0" max="1" step="any" bind:value={$reliefFactor} />
      </label>
      <button
        on:click|preventDefault={() => {
          selectedOverlay.set(
            addTopoInfluence(1.0, centeredTransform(0.3)),
          );
        }}
      >
        + Add influence
      </button>
    </ControlPanel>
  {:else if $mode === "colors"}
    <ControlPanel title="Colors" icon={BrushLine}>
      <PaletteInput bind:palette={$palette} on:input={() => ($paletteName = "custom")} />
    </ControlPanel>
    <ControlPanel title="Presets" icon={BrushLine}>
      <div class="palettes">
        {#each Object.entries(palettes) as [name, colors] (name)}
          <button
            class="palette-pick"
            class:active={$paletteName === name}
            title={name}
            on:click|preventDefault={() => ($paletteName = name)}
          >
            <span class="palette-name">{name}</span>
            <span class="swatches">
              {#each colors as color}
                <span class="swatch" style="background: {color}" />
              {/each}
            </span>
          </button>
        {/each}
      </div>
    </ControlPanel>
  {:else if $mode === "effects"}
    <ControlPanel title="Map" icon={MagicLine}>
      <label class="range">
        Border
        <input type="range" min="0" max="1" step="any" bind:value={$borderFactor} />
      </label>
      <label class="range">
        Resolution
        <input type="range" min="0.25" max="1" step="any" bind:value={$resolutionScale} />
      </label>
      <label class="range">
        Vignette
        <input type="range" min="0" max="1" step="any" bind:value={$vignette} />
      </label>
      <label class="range">
        Grain
        <input type="range" min="0" max="0.5" step="any" bind:value={$grain} />
      </label>
    </ControlPanel>
    <ControlPanel title="Waves" icon={MagicLine}>
      <label>
        Style
        <select bind:value={$waveStyle}>
          <option value={1}>Sine</option>
          <option value={2}>V</option>
          <option value={3}>Grid</option>
          <option value={4}>Seigaiha</option>
        </select>
      </label>
      <label class="range">
        Opacity
        <input type="range" min="0" max="0.25" step="any" bind:value={$waveOpacity} />
      </label>
      <label class="range">
        Thickness
        <input type="range" min="0.02" max="0.5" step="any" bind:value={$waveThickness} />
      </label>
      <label class="range">
        Speed
        <input type="range" min="0" max="1" step="any" bind:value={$waveSpeed} />
      </label>
      <label class="range">
        Scale
        <input type="range" min="0.25" max="4" step="any" bind:value={$waveScale} />
      </label>
    </ControlPanel>
  {:else if $mode === "labels"}
    <ControlPanel title="Labels" icon={MapPin2Line}>
      <button
        on:click|preventDefault={() => {
          selectedOverlay.set(addLabel("New label"));
        }}
      >
        + Add label
      </button>
    </ControlPanel>
  {:else if $mode === "stickers"}
    <ControlPanel title="Stickers" icon={Compass3Line}>
      <input type="search" placeholder="Search..." bind:value={stickerSearch} />
      {#if $gameIcons}
        <div class="sticker-grid">
          {#each stickerResults as name (name)}
            <button
              class="sticker-pick"
              title={name}
              on:click|preventDefault={() => selectedOverlay.set(addSticker(name))}
            >
              <svg viewBox={ICON_VIEWBOX}>{@html $gameIcons.icons[name]}</svg>
            </button>
          {/each}
        </div>
        {#if stickerMatches.length > stickerResults.length}
          <div class="hint">
            Showing {stickerResults.length} of {stickerMatches.length}
          </div>
        {:else if stickerMatches.length === 0}
          <div class="hint">No results</div>
        {/if}
      {:else}
        <div class="hint">Loading icons…</div>
      {/if}
    </ControlPanel>
  {/if}

  {#if $selectedOverlay}
    <ControlPanel>
      <div><code>{$selectedOverlay.name}</code></div>
      {#if $selectedOverlay.type === "label"}
        <textarea on:keyup={() => overlays.set($overlays)} bind:value={$selectedOverlay.text} />
        <label class="range">
          Size
          <input
            type="range"
            min="0"
            max="5"
            step="any"
            bind:value={$selectedOverlay.fontSizeRem}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <label class="range">
          Zooming
          <input
            type="range"
            min="0"
            max="1"
            step="any"
            bind:value={$selectedOverlay.zooming}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <label class="range">
          Tracking
          <input
            type="range"
            min="0"
            max="5"
            step="any"
            bind:value={$selectedOverlay.letterSpacingRem}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <label class="range">
          Rotation
          <input
            type="range"
            min="-180"
            max="180"
            step="any"
            bind:value={$selectedOverlay.rotationDeg}
            on:input={() => overlays.set($overlays)}
            list="rotations"
          />
          <datalist id="rotations">
            <option value="-135" />
            <option value="-90" />
            <option value="-45" />
            <option value="0" />
            <option value="45" />
            <option value="90" />
            <option value="135" />
          </datalist>
        </label>
        <select on:change={() => overlays.set($overlays)} bind:value={$selectedOverlay.textAlign}>
          <option value="left">text left</option>
          <option value="center">text center</option>
          <option value="right">text right</option>
        </select>
        <select on:change={() => overlays.set($overlays)} bind:value={$selectedOverlay.anchor}>
          <option value="nw">anchor top left</option>
          <option value="n">anchor top</option>
          <option value="ne">anchor top right</option>
          <option value="w">anchor left</option>
          <option value="c">anchor center</option>
          <option value="e">anchor right</option>
          <option value="sw">anchor bottom left</option>
          <option value="s">anchor bottom</option>
          <option value="se">anchor bottom right</option>
        </select>
        <button on:click={duplicateSelectedOverlay}>Duplicate</button>
        <button on:click={deleteSelectedOverlay}>Delete label</button>
      {:else if $selectedOverlay.type === "topoInfluence"}
        <label class="range">
          X
          <input
            type="range"
            min="-0.5"
            max="1.5"
            step="any"
            bind:value={$selectedOverlay.transform[6]}
            on:input={() => {overlays.set($overlays)}}
            />
        </label>
        <label class="range">
          Y
          <input
            type="range"
            min="-0.5"
            max="1.5"
            step="any"
            bind:value={$selectedOverlay.transform[7]}
            on:input={() => overlays.set($overlays)}
            />
        </label>
        <label class="range">
          Size
          <input
            type="range"
            min="0.05"
            max="1"
            step="any"
            value={$selectedOverlay.transform[0]}
            on:input={(e) => {
              $selectedOverlay.transform[0] = e.target.value;
              $selectedOverlay.transform[4] = e.target.value;
              overlays.set($overlays);
            }}
            />
        </label>
        <label class="range">
          Strength
          <input
            type="range"
            min="-2"
            max="2"
            step="0.1"
            bind:value={$selectedOverlay.factor}
            on:input={() => overlays.set($overlays)}
            />
        </label>
        <label>
          Tint
          <input
            type="color"
            bind:value={$selectedOverlay.color}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <button on:click={duplicateSelectedOverlay}>Duplicate</button>
        <button on:click={deleteSelectedOverlay}>Delete influence</button>
      {:else if $selectedOverlay.type === "sticker"}
        <label class="range">
          Size
          <input
            type="range"
            min="1"
            max="12"
            step="any"
            bind:value={$selectedOverlay.sizeRem}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <label class="range">
          Zooming
          <input
            type="range"
            min="0"
            max="1"
            step="any"
            bind:value={$selectedOverlay.zooming}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <label class="range">
          Rotation
          <input
            type="range"
            min="-180"
            max="180"
            step="any"
            bind:value={$selectedOverlay.rotationDeg}
            on:input={() => overlays.set($overlays)}
            list="rotations"
          />
          <datalist id="rotations">
            <option value="-135" />
            <option value="-90" />
            <option value="-45" />
            <option value="0" />
            <option value="45" />
            <option value="90" />
            <option value="135" />
          </datalist>
        </label>
        <label>
          Color
          <input
            type="color"
            bind:value={$selectedOverlay.color}
            on:input={() => overlays.set($overlays)}
          />
        </label>
        <select on:change={() => overlays.set($overlays)} bind:value={$selectedOverlay.anchor}>
          <option value="nw">anchor top left</option>
          <option value="n">anchor top</option>
          <option value="ne">anchor top right</option>
          <option value="w">anchor left</option>
          <option value="c">anchor center</option>
          <option value="e">anchor right</option>
          <option value="sw">anchor bottom left</option>
          <option value="s">anchor bottom</option>
          <option value="se">anchor bottom right</option>
        </select>
        <button on:click={duplicateSelectedOverlay}>Duplicate</button>
        <button on:click={deleteSelectedOverlay}>Delete sticker</button>
      {:else}
        <div>(to do...)</div>
      {/if}
    </ControlPanel>
  {/if}

  {#if !$mode}
    <MenuButton icon={ShuffleLine} label="Random" on:click={randomize} />
    <MenuButton icon={EarthLine} label="Topography" on:click={() => mode.set("topography")} />
    <MenuButton icon={BrushLine} label="Colors" on:click={() => mode.set("colors")} />
    <MenuButton icon={MapPin2Line} label="Labels" on:click={() => mode.set("labels")} />
    <MenuButton icon={Compass3Line} label="Stickers" on:click={() => mode.set("stickers")} />
    <MenuButton icon={MagicLine} label="Effects" on:click={() => mode.set("effects")} />
    <MenuButton icon={ShareLine} label={shareLabel} on:click={shareUrl} />
    <MenuButton
      icon={isFullscreen ? FullscreenExitLine : FullscreenLine}
      label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
      on:click={toggleFullscreen}
    />
    <MenuButton icon={QuestionLine} label="Help" on:click={() => mode.set("help")} />
  {/if}
  {/if}
</div>

<style>
  .controls {
    position: absolute;
    box-sizing: border-box;
    padding: 10px;
    padding-left: 12rem;
    right: 10px;
    top: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    pointer-events: none;
    /* Cap to the viewport (dynamic units handle mobile URL bars) so a tall menu
       scrolls instead of running off the bottom on short screens. */
    max-height: calc(100dvh - 20px);
    overflow-y: auto;
    /* Scroll, but never show the scrollbar. */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* old Edge/IE */
  }
  .controls::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }
  input[type="search"] {
    padding: 6px;
    border: 0;
    border-radius: 2px;
    background: rgb(15, 40, 66);
    color: white;
  }
  /* Two shared columns — name (auto) and swatch strip (rest). */
  .palettes {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 5px 8px;
  }
  /* Each tile spans both columns and adopts them via subgrid, so names and
     swatch strips align across every preset. (Overrides the global button row.) */
  .palette-pick {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    align-items: center;
    padding: 4px 6px;
    cursor: pointer;
  }
  .palette-pick.active {
    outline: 2px solid #bfbdb6;
  }
  .palette-name {
    text-align: left;
    font-size: 0.85em;
  }
  .swatches {
    display: flex;
    height: 20px;
    border-radius: 2px;
    overflow: hidden;
  }
  .swatch {
    flex: 1;
  }
  .sticker-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 4px;
    max-height: 260px;
    overflow-y: auto;
    padding: 2px;
  }
  .sticker-pick {
    padding: 4px;
    aspect-ratio: 1 / 1;
  }
  .sticker-pick svg {
    width: 100%;
    height: 100%;
    color: white;
    fill: white;
  }
  .hint {
    text-align: center;
  }
  input[type="color"] {
    width: 60px;
    height: 24px;
    background: none;
    border: 0;
    padding: 0;
  }
  input[type="color"]::-webkit-color-swatch {
    border: 2px solid rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
  input[type="color"]::-moz-color-swatch {
    border: 2px solid rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
  input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
  }
</style>
